/**
 * Purpose: Manages service worker lifecycle and notification interactions
 *
 * How it works:
 * 1. Handles service worker updates and version detection
 * 2. Processes notification clicks from the service worker
 *
 * Key features:
 * - Service worker update detection and user prompts
 * - Notification click handling with navigation via setActiveChatId
 *
 * Note: Auto-join for meeting notifications (?action=join) was intentionally removed.
 * Clicking a meeting notification now navigates to the chat; the user joins manually.
 */

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "@/zustand/AuthState";
import { postTokenToServiceWorker } from "@/service-worker/swToken";

/**
 * Forces the waiting service worker to become active
 * Used when user accepts the update prompt
 */
const updateServiceWorker = () => {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach(async (registration) => {
      if (!registration.waiting) {
        return;
      }
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    });
  });
};

const useSWLifeCycle = () => {
  // State
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  // Navigation
  const navigate = useNavigate();

  // ===================
  // Navigation Helpers
  // ===================

  /**
   * Navigates to a URL, using React Router for internal URLs
   */
  const navigateToUrl = useCallback(
    (url: URL) => {
      if (url.origin === globalThis.location.origin) {
        // Internal navigation - use React Router
        navigate(url.pathname + url.search + url.hash);
      } else {
        // External navigation - use full page reload
        globalThis.location.href = url.href;
      }
    },
    [navigate],
  );

  // ===================
  // Service Worker Update Management
  // ===================

  /**
   * Handles service worker state changes
   * Shows update prompt when new version is installed
   */
  const handleNewWorkerStateChange = useCallback((newWorker: ServiceWorker) => {
    if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
      setNewVersionAvailable(true);
    }
  }, []);

  /**
   * Sets up listener for when a new service worker is found
   */
  const handleUpdateFound = useCallback(
    (registration: ServiceWorkerRegistration) => {
      const newWorker = registration.installing;
      if (!newWorker) {
        return;
      }

      newWorker.addEventListener("statechange", () =>
        handleNewWorkerStateChange(newWorker),
      );
    },
    [handleNewWorkerStateChange],
  );

  /**
   * Initializes service worker update tracking
   */
  const trackServiceWorkerUpdates = useCallback(async () => {
    const registration = await navigator.serviceWorker.ready;

    // Listen for new workers
    registration.addEventListener("updatefound", () =>
      handleUpdateFound(registration),
    );

    // Check if there's already a waiting worker
    if (
      registration.waiting?.state === "installed" &&
      navigator.serviceWorker.controller
    ) {
      setNewVersionAvailable(true);
    }
  }, [handleUpdateFound]);

  // ===================
  // Effects
  // ===================

  /**
   * Effect: Handle notification clicks from service worker
   */
  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      // Security: Only accept messages from service workers.
      // The navigator.serviceWorker "message" event only fires for messages
      // from SWs, so we just verify event.source is a ServiceWorker instance.
      // Avoid async registration lookup which causes identity mismatches
      // (especially in PWA + push notification scenarios).
      if (!(event.source instanceof ServiceWorker)) {
        return;
      }

      if (event.data.type === "NOTIFICATION_CLICK") {
        // Acknowledge so the service worker knows a live client handled the
        // click and does not fall back to a full URL navigation (which would
        // reload the app). The SW transfers a MessagePort for this purpose.
        const ackPort = event.ports?.[0];
        if (ackPort) {
          try {
            ackPort.postMessage({ type: "NOTIFICATION_CLICK_ACK" });
          } catch {
            // Ignore — SW will fall back to URL navigation.
          }
        }

        const chatId = event.data.chatId as string | undefined;

        if (chatId) {
          navigate(`/${chatId}`, { replace: true });
        } else {
          // Fallback: URL-based navigation
          const url = new URL(event.data.url, globalThis.location.origin);
          navigateToUrl(url);
        }
      }
    };

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener("message", handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [navigateToUrl, navigate]);

  /**
   * Effect: Handle fallback notification clicks (dev mode, no SW)
   * appNotification.ts uses postMessage when SW is unavailable
   */
  useEffect(() => {
    const handleFallbackNotificationClick = (event: MessageEvent) => {
      // Only accept messages from our own origin and window context (defense-in-depth)
      if (event.origin !== globalThis.location.origin) {
        return;
      }
      if (event.source !== globalThis.window) {
        return;
      }
      if (
        event.data?.source !== "app-notification" ||
        event.data?.type !== "notification-click"
      ) {
        return;
      }

      const chatId = event.data.chatId as string | undefined;
      if (chatId) {
        navigate(`/${chatId}`, { replace: true });
      } else if (event.data.url) {
        const url = new URL(event.data.url, globalThis.location.origin);
        navigateToUrl(url);
      }
    };

    globalThis.addEventListener("message", handleFallbackNotificationClick);

    return () => {
      globalThis.removeEventListener(
        "message",
        handleFallbackNotificationClick,
      );
    };
  }, [navigate, navigateToUrl]);

  /**
   * Effect: Initialize service worker update tracking
   */
  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    // Reload page when service worker updates
    navigator.serviceWorker.addEventListener("controllerchange", () =>
      globalThis.location.reload(),
    );

    // Start tracking for updates
    trackServiceWorkerUpdates();
  }, [trackServiceWorkerUpdates]);

  /**
   * Effect: Keep the service worker's bearer token fresh.
   * Pushes the current token once the SW controls the page, then on every auth
   * token change (login + silent refresh), so long-running chunked transfers
   * don't 401 when the original JWT snapshot expires. See swToken.ts.
   */
  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }
    void navigator.serviceWorker.ready.then(() =>
      postTokenToServiceWorker(useAuthState.getState().tk),
    );
    const unsubscribe = useAuthState.subscribe((state, prev) => {
      if (state.tk !== prev.tk) {
        postTokenToServiceWorker(state.tk);
      }
    });
    return unsubscribe;
  }, []);

  return { newVersionAvailable, updateServiceWorker };
};

export default useSWLifeCycle;
